import React, { useCallback, useEffect, useState } from 'react';
import SafeHtml from '../../components/SafeHtml';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { dirAttr, resolveLocalized } from '../../i18n/contentLocales';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { listRunChallenges, listSubmissions, updateSubmission } from '../../api/runs';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { MultilingualString, RunChallenge, Submission } from '../../api/types';
import ErrorAlert from '../../components/ErrorAlert';
import SubmittedAnswer from '../../components/SubmittedAnswer';
import LoadingSpinner from '../../components/LoadingSpinner';
import BackLink from '../../components/BackLink';
import TextChallengeForm from './TextChallengeForm';
import QuizChallengeForm from './QuizChallengeForm';
import QrScanChallengeForm from './QrScanChallengeForm';
import PhotoChallengeForm from './PhotoChallengeForm';
import VideoChallengeForm from './VideoChallengeForm';
import AudioChallengeForm from './AudioChallengeForm';
import CreativeChallengeForm from './CreativeChallengeForm';
import type { ChallengeFormProps } from './TextChallengeForm';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const ChallengeView: React.FC = () => {
  const { runId, stationId, challengeId } = useParams<{
    runId: string;
    stationId: string;
    challengeId: string;
  }>();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('runs');

  const [challenge, setChallenge] = useState<RunChallenge | null>(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submission, setSubmission] = useState<Submission | null>(null);
  // Replace mode (issue #255): re-enable the form so the team can swap its
  // unscored answer. Exits on successful replacement or cancel.
  const [replaceMode, setReplaceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgSlug = user?.organization_slug;

  const load = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!orgSlug || !runId || !stationId || !challengeId) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [challengesResp, submissionsResp] = await Promise.all([
        listRunChallenges(orgSlug, runId, stationId),
        listSubmissions(orgSlug, runId),
      ]);
      const found = challengesResp.items.find((c) => c.id === challengeId) ?? null;
      setChallenge(found);
      const existing = submissionsResp.items.find(
        (s: Submission) => s.run_challenge_id === challengeId,
      );
      if (existing) {
        setAlreadySubmitted(true);
        setSubmission(existing);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, runId, stationId, challengeId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Scoring happens elsewhere and emits no live signal, so an open challenge
  // would otherwise keep claiming the answer is merely submitted.
  useAutoRefresh(() => load({ quiet: true }));

  // Full UI language (not collapsed to de/en): org content locales are free
  // (e.g. ar, fa) — an Arabic-browser participant gets Arabic content, RTL.
  const activeLang = i18n.language;
  const titleOf = (ml: MultilingualString) => resolveLocalized(ml, activeLang).text;
  const dirOf = (ml: MultilingualString) => dirAttr(resolveLocalized(ml, activeLang).locale);

  const handleSubmitted = (result: Submission) => {
    setSubmission(result);
    setAlreadySubmitted(true);
    setReplaceMode(false);
    // The answered view replaces a taller form, and the browser keeps the
    // offset it had at the submit button — which left people looking at the
    // middle of the page, with the confirmation and the way back to the
    // station both above the fold.
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  if (loading) return <LoadingSpinner />;

  if (!challenge) {
    return <ErrorAlert message={error ?? 'Challenge not found'} onRetry={load} />;
  }

  const formProps: ChallengeFormProps = {
    orgSlug: orgSlug ?? '',
    runId: runId ?? '',
    challenge,
    alreadySubmitted: alreadySubmitted && !replaceMode,
    onSubmitted: handleSubmitted,
    ...(replaceMode && submission
      ? {
          submitAction: (content: Submission['content']) =>
            updateSubmission(orgSlug ?? '', runId ?? '', submission.id, { content }),
          initialContent: submission.content,
        }
      : {}),
  };

  const renderForm = () => {
    switch (challenge.challenge_type) {
      case 'text':
        return <TextChallengeForm {...formProps} />;
      case 'quiz':
        return <QuizChallengeForm {...formProps} />;
      case 'qr_scan':
        return <QrScanChallengeForm {...formProps} />;
      case 'photo':
        return <PhotoChallengeForm {...formProps} />;
      case 'video':
        return <VideoChallengeForm {...formProps} />;
      case 'audio':
        return <AudioChallengeForm {...formProps} />;
      case 'creative':
        return <CreativeChallengeForm {...formProps} />;
      default:
        return <ErrorAlert message={`Unsupported challenge type: ${challenge.challenge_type}`} />;
    }
  };

  // Color the type chip per the Figma type-pill palette so the chip
  // visually anchors the challenge to its kind.
  const typeChipColor: Record<RunChallenge['challenge_type'], 'primary' | 'secondary' | 'tertiary' | 'default'> = {
    text: 'primary',
    quiz: 'tertiary',
    qr_scan: 'secondary',
    photo: 'primary',
    creative: 'secondary',
    audio: 'secondary',
    video: 'primary',
  };

  // Mirrors the backend predicate in services/auto_score.py: auto_approve is
  // only honoured for QUIZ and QR_SCAN. On any other type the flag can survive
  // a type change and does nothing (App#459), so checking it alone would tell
  // participants their text answer was auto-scored when a human scored it.
  const isAutoScored =
    challenge.scoring_rules.auto_approve === true &&
    (challenge.challenge_type === 'quiz' || challenge.challenge_type === 'qr_scan');

  // Three distinct situations that all used to render the same line. Auto-scored
  // answers come back graded in the same breath as the submission, so "submitted"
  // alone reads as if nothing happened; an answer waiting on a coordinator should
  // say so; a coordinator-scored one keeps the neutral wording and lets the
  // scoredLocked line below carry the detail. (#413, Toni T1-6)
  const submittedMessageKey = !submission?.is_scored
    ? 'participant.challenge.successPendingReview'
    : isAutoScored
      ? 'participant.challenge.successAutoScored'
      : 'participant.challenge.successGeneric';

  // Once an answer exists the authored instructions have done their job and
  // just push the answer off screen — e.g. "Wähle die richtige Antwort" above
  // an already-answered quiz. They come back while replacing, when they are
  // needed again. (#413, Toni T1-5)
  const showInstructions = !submission || replaceMode;

  return (
    <Stack spacing={2}>
      <BackLink
        to={`/runs/${runId}/participant/stations/${stationId}`}
        label={t('participant.challenge.backToStation')}
      />
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        {/*
          Intentional vocabulary split (not a bug): players see friendly prompt
          names via `participant.challenge.types.*` (e.g. text → "Question",
          quiz → "Multiple Choice"), while authors see the technical type names
          via `challenge.types.*` ("Text", "Quiz"). Keep the two namespaces
          distinct; don't "align" them.
        */}
        <Chip
          label={t(`participant.challenge.types.${challenge.challenge_type}`, {
            defaultValue: challenge.challenge_type,
          })}
          size="small"
          color={typeChipColor[challenge.challenge_type] ?? 'default'}
        />
        {typeof challenge.scoring_rules.max_points === 'number' && (
          <Chip
            label={t('participant.challenge.points', {
              points: challenge.scoring_rules.max_points,
            })}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>
      <Typography variant="h2" component="h1" dir={dirOf(challenge.title)}>
        {titleOf(challenge.title) || t('participant.challenge.untitled', { defaultValue: 'Aufgabe' })}
      </Typography>

      {error && <ErrorAlert message={error} />}

      {titleOf(challenge.instructions) && showInstructions && (
        <Card
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            border: 'none',
          }}
        >
          <CardContent>
            <Typography
              variant="overline"
              gutterBottom
              sx={{ display: 'block', opacity: 0.85, letterSpacing: 0.6 }}
            >
              {t('participant.challenge.instructions')}
            </Typography>
            <SafeHtml
              html={titleOf(challenge.instructions)}
              dir={dirOf(challenge.instructions)}
              sx={{ '& a': { color: 'inherit', textDecorationColor: 'inherit' } }}
            />
          </CardContent>
        </Card>
      )}

      {submission && (
        <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
          <CardContent>
            <Typography variant="body2" color="success.main">
              {t(submittedMessageKey)}
            </Typography>
            {/* What was actually submitted, before what can be done about it —
                reading your own answer must not require entering the edit flow. */}
            {!replaceMode && <SubmittedAnswer content={submission.content} />}
            {!submission.is_scored && !replaceMode && (
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1.5 }}
                onClick={() => setReplaceMode(true)}
              >
                {t('participant.challenge.changeAnswer')}
              </Button>
            )}
            {!submission.is_scored && replaceMode && (
              <Button
                variant="text"
                size="small"
                sx={{ mt: 1.5 }}
                onClick={() => setReplaceMode(false)}
              >
                {t('participant.challenge.changeCancel')}
              </Button>
            )}
            {submission.is_scored && (
              <>
                {/* The result first — a team opens a graded challenge to learn
                    what it scored, not to read that it may no longer edit. */}
                {submission.points !== null && (
                  <Chip
                    icon={<EmojiEventsIcon />}
                    color="success"
                    size="small"
                    sx={{ mt: 1.5 }}
                    label={t('participant.challenge.scoredWithPoints', {
                      points: submission.points,
                      maxPoints: submission.max_points ?? challenge.scoring_rules.max_points ?? 0,
                    })}
                  />
                )}
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  {t(
                    isAutoScored
                      ? 'participant.challenge.autoScoredLocked'
                      : 'participant.challenge.scoredLocked',
                  )}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Divider />

      {/* Key forces a fresh form mount when toggling replace mode so the
          prefill (initialContent) is picked up by the form's initial state. */}
      <Box key={replaceMode ? 'replace' : 'initial'}>{renderForm()}</Box>
    </Stack>
  );
};

export default ChallengeView;
